(() => {
  // Version marker: restore customizer interaction loading after theme asset refresh.
  const stateStore = window.__jerseyCustomizerState || (window.__jerseyCustomizerState = new Map());

  function primePatchDrawerBeforePaint(root) {
    if (!root || !root.querySelectorAll) return;

    const customizers = [];
    if (root.matches?.('jersey-customizer')) customizers.push(root);
    customizers.push(...root.querySelectorAll('jersey-customizer'));

    customizers.forEach((customizer) => {
      const stateKey = `${customizer.dataset.productId}:${customizer.dataset.formId}`;
      const state = stateStore.get(stateKey);
      if (!state?.patchOpen) return;

      const card = customizer.querySelector('[data-jc-patch-card]');
      if (!card) return;
      card.classList.add('is-open');
      customizer.querySelector('[data-jc-patch-toggle]')?.setAttribute('aria-expanded', 'true');

      const selectedPatches = Array.isArray(state.patches) ? state.patches : [];
      customizer.querySelectorAll('[data-jc-patch]').forEach((input) => {
        input.checked = selectedPatches.includes(input.value);
      });
    });
  }

  const patchDrawerPaintGuard = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => primePatchDrawerBeforePaint(node));
    });
  });
  patchDrawerPaintGuard.observe(document.documentElement, { childList: true, subtree: true });

  function mountCartFlight(root = document) {
    root.querySelectorAll('product-info .product-form__submit[name="add"]').forEach((button) => {
      if (button.dataset.jcCartFlightMounted === 'true') return;

      const host = button.parentElement;
      if (!host) return;

      button.dataset.jcCartFlightMounted = 'true';
      button.classList.add('jc-cart-flight-button');
      host.classList.add('jc-cart-flight-host');

      const track = document.createElement('span');
      const icon = document.createElement('span');
      const cart = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      track.className = 'jc-cart-flight-track';
      icon.className = 'jc-cart-flight-icon';
      cart.classList.add('jc-cart-flight-cart');
      cart.setAttribute('viewBox', '0 0 72 56');
      cart.setAttribute('focusable', 'false');
      cart.setAttribute('aria-hidden', 'true');
      cart.innerHTML = `
        <path d="M4 14h15M8 26h14M4 38h17"
          fill="none" stroke="currentColor" stroke-width="5"
          stroke-linecap="round" />
        <path d="M25 8h7.2c1.7 0 3.1 1.1 3.5 2.7l1.7 6.3h25.8c2.5 0 4.2 2.5 3.3 4.8l-6 16.1c-.5 1.3-1.8 2.1-3.2 2.1H39.1c-1.7 0-3.1-1.1-3.5-2.7L30 14h-5Z"
          fill="currentColor" />
        <circle cx="41" cy="48" r="4.3" fill="currentColor" />
        <circle cx="56" cy="48" r="4.3" fill="currentColor" />
      `;
      track.setAttribute('aria-hidden', 'true');
      icon.setAttribute('aria-hidden', 'true');
      icon.append(cart);
      host.append(track);
      button.append(icon);

      const syncTrack = () => {
        const buttonRect = button.getBoundingClientRect();
        const hostRect = host.getBoundingClientRect();
        track.style.setProperty('--jc-cart-flight-left', `${buttonRect.left - hostRect.left}px`);
        track.style.setProperty('--jc-cart-flight-top', `${buttonRect.top - hostRect.top}px`);
        track.style.setProperty('--jc-cart-flight-width', `${buttonRect.width}px`);
        track.style.setProperty('--jc-cart-flight-height', `${buttonRect.height}px`);
      };

      syncTrack();
      if ('ResizeObserver' in window) {
        const observer = new ResizeObserver(syncTrack);
        observer.observe(button);
        observer.observe(host);
        track.jcCartFlightResizeObserver = observer;
      }
    });
  }

  class JerseyCustomizer extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true' || this.dataset.initPending === 'true') return;

      this.dataset.initPending = 'true';
      let readinessObserver;
      const initialize = () => {
        readinessObserver?.disconnect();
        delete this.dataset.initPending;
        if (!this.isConnected || this.dataset.initialized === 'true') return;
        this.initialize();
      };

      // The customizer is rendered immediately after the Customize option, while
      // the Patch charge option can still be waiting for the HTML parser below it.
      // Initialize as soon as those native controls exist instead of waiting for
      // every product image and third-party app to finish parsing.
      const picker = this.closest('variant-picker');
      const controlsReady = () => {
        if (!picker?.querySelector('.jc-native-customize-option')) return false;
        return !this.querySelector('[data-jc-patch-card]')
          || Boolean(picker.querySelector('.jc-native-patch-option'));
      };

      if (controlsReady()) {
        queueMicrotask(initialize);
        return;
      }

      readinessObserver = new MutationObserver(() => {
        if (controlsReady()) initialize();
      });
      readinessObserver.observe(picker || document.documentElement, { childList: true, subtree: true });

      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initialize, { once: true });
      } else {
        requestAnimationFrame(initialize);
      }
    }

    initialize() {
      this.dataset.initialized = 'true';

      this.formId = this.dataset.formId;
      this.stateKey = `${this.dataset.productId}:${this.formId}`;
      this.patchLimit = Math.max(1, Math.min(3, Number(this.dataset.patchLimit) || 1));
      this.customPriceCents = Number(this.dataset.customPriceCents) || 500;
      this.variantPicker = this.closest('variant-picker');
      this.nativeOption = this.variantPicker?.querySelector('.jc-native-customize-option');
      this.nativePatchOption = this.variantPicker?.querySelector('.jc-native-patch-option');
      this.yesRadio = this.findNativeRadio('yes');
      this.noRadio = this.findNativeRadio('no');
      this.patchNoneRadio = this.findPatchRadio(0);
      this.patchOneRadio = this.findPatchRadio(1);
      this.patchTwoRadio = this.findPatchRadio(2);
      this.patchThreeRadio = this.findPatchRadio(3);

      const pricedPatchCounts = [
        [1, this.patchOneRadio],
        [2, this.patchTwoRadio],
        [3, this.patchThreeRadio]
      ].filter(([, radio]) => Boolean(radio)).map(([count]) => count);
      if (pricedPatchCounts.length > 0) {
        this.patchLimit = Math.min(this.patchLimit, Math.max(...pricedPatchCounts));
      }
      const patchLimitLabel = this.querySelector('.jc-patch-heading span');
      if (patchLimitLabel) patchLimitLabel.textContent = `(Up to ${this.patchLimit})`;

      this.variantDataScript = this.querySelector('[data-jc-variant-map]');
      try {
        this.variantMap = JSON.parse(this.variantDataScript?.textContent || '[]');
      } catch (_) {
        this.variantMap = [];
      }

      this.customCard = this.querySelector('[data-jc-custom-card]');
      this.customBody = this.querySelector('[data-jc-custom-body]');
      this.customSummary = this.querySelector('[data-jc-custom-summary]');
      this.customProperty = this.querySelector('[data-jc-custom-property]');
      this.customAdd = this.querySelector('[data-jc-custom-add]');
      this.nameInput = this.querySelector('[data-jc-name]');
      this.numberInput = this.querySelector('[data-jc-number]');
      this.playerSelect = this.querySelector('[data-jc-player-select]');
      this.patchInputs = Array.from(this.querySelectorAll('[data-jc-patch]'));
      this.patchProperty = this.querySelector('[data-jc-patch-property]');
      this.patchSummary = this.querySelector('[data-jc-patch-summary]');
      this.patchAdd = this.querySelector('[data-jc-patch-add]');
      this.drawerAnimations = new WeakMap();

      this.restoreState();
      this.setupPlayerPicker();
      this.bindEvents();
      this.syncCustomizationState();
      this.evaluateCustomization({ allowVariantChange: true });
      this.updatePatchState({ allowVariantChange: true });

      const prewarm = () => this.warmPatchImages();
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(prewarm, { timeout: 1400 });
      } else {
        window.setTimeout(prewarm, 650);
      }
    }

    setupPlayerPicker() {
      if (!this.playerSelect || this.querySelector('[data-jc-player-picker]')) return;

      const nativeWrap = this.playerSelect.closest('.jc-select-wrap');
      const field = nativeWrap?.closest('.jc-field');
      if (!nativeWrap || !field) return;

      const picker = document.createElement('div');
      const trigger = document.createElement('button');
      const triggerLabel = document.createElement('span');
      const triggerIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      const menu = document.createElement('div');
      const mobileField = document.createElement('div');
      const mobileFieldLabel = document.createElement('span');
      const menuId = `JerseyPlayerPicker-${this.formId}`;

      mobileField.className = 'jc-field jc-player-picker-field';
      mobileFieldLabel.textContent = field.querySelector('span')?.textContent.trim() || 'Select player';
      picker.className = 'jc-player-picker';
      picker.dataset.jcPlayerPicker = '';
      trigger.className = 'jc-player-picker__trigger';
      trigger.type = 'button';
      trigger.dataset.jcPlayerTrigger = '';
      trigger.setAttribute('aria-haspopup', 'listbox');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', menuId);
      triggerLabel.dataset.jcPlayerPickerLabel = '';
      triggerIcon.setAttribute('viewBox', '0 0 20 20');
      triggerIcon.setAttribute('fill', 'none');
      triggerIcon.setAttribute('aria-hidden', 'true');
      triggerIcon.innerHTML = '<path d="m5 7.5 5 5 5-5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>';
      trigger.append(triggerLabel, triggerIcon);

      menu.className = 'jc-player-picker__menu';
      menu.dataset.jcPlayerMenu = '';
      menu.id = menuId;
      menu.setAttribute('role', 'listbox');
      menu.setAttribute('aria-hidden', 'true');
      menu.setAttribute('inert', '');

      Array.from(this.playerSelect.options).forEach((nativeOption) => {
        const option = document.createElement('button');
        const optionLabel = document.createElement('span');
        const optionCheck = document.createElement('span');
        option.className = 'jc-player-picker__option';
        option.type = 'button';
        option.dataset.jcPlayerOption = '';
        option.dataset.playerValue = nativeOption.value;
        option.setAttribute('role', 'option');
        option.tabIndex = -1;
        optionLabel.textContent = nativeOption.textContent.trim();
        optionCheck.className = 'jc-player-picker__check';
        optionCheck.textContent = '✓';
        optionCheck.setAttribute('aria-hidden', 'true');
        option.append(optionLabel, optionCheck);
        option.addEventListener('click', () => {
          this.playerSelect.value = nativeOption.value;
          this.syncPlayerPicker();
          this.setPlayerPickerOpen(false);
          this.playerSelect.dispatchEvent(new Event('change', { bubbles: true }));
        });
        menu.append(option);
      });

      trigger.addEventListener('click', () => {
        this.setPlayerPickerOpen(!picker.classList.contains('is-open'));
      });
      picker.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape') return;
        event.preventDefault();
        this.setPlayerPickerOpen(false);
        trigger.focus();
      });

      picker.append(trigger, menu);
      mobileField.append(mobileFieldLabel, picker);
      field.after(mobileField);
      field.classList.add('has-mobile-player-picker');
      this.playerPicker = picker;
      this.playerPickerTrigger = trigger;
      this.playerPickerLabel = triggerLabel;
      this.playerPickerMenu = menu;
      this.playerPickerOptions = Array.from(menu.querySelectorAll('[data-jc-player-option]'));
      this.syncPlayerPicker();
    }

    setPlayerPickerOpen(open) {
      if (!this.playerPicker || !this.playerPickerMenu || !this.playerPickerTrigger) return;
      const shouldOpen = Boolean(open && !this.playerPickerTrigger.disabled);
      this.playerPicker.classList.toggle('is-open', shouldOpen);
      this.playerPickerMenu.setAttribute('aria-hidden', String(!shouldOpen));
      this.playerPickerMenu.toggleAttribute('inert', !shouldOpen);
      this.playerPickerTrigger.setAttribute('aria-expanded', String(shouldOpen));
      this.playerPickerOptions?.forEach((option) => {
        option.tabIndex = shouldOpen ? 0 : -1;
      });
    }

    syncPlayerPicker() {
      if (!this.playerSelect || !this.playerPickerTrigger || !this.playerPickerLabel) return;
      const selected = this.playerSelect.selectedOptions[0];
      const value = selected?.value || '';
      this.playerPickerLabel.textContent = selected?.textContent.trim() || 'Select a player';
      this.playerPickerTrigger.disabled = this.playerSelect.disabled;
      this.playerPickerOptions?.forEach((option) => {
        option.setAttribute('aria-selected', String(option.dataset.playerValue === value));
      });
    }

    findNativeRadio(kind) {
      return Array.from(this.nativeOption?.querySelectorAll('input[type="radio"]') || []).find((radio) => {
        const value = String(radio.value || '').trim().toLowerCase();
        return kind === 'yes' ? /^yes\b/.test(value) : /^no\b/.test(value);
      });
    }

    findPatchRadio(count) {
      return Array.from(this.nativePatchOption?.querySelectorAll('input[type="radio"]') || []).find((radio) => {
        const value = String(radio.value || '').trim().toLowerCase();
        if (count === 0) return /^(none|no\s*patch|0\s*patch)/.test(value);
        if (count === 1) return /^(1|one|single)\b/.test(value);
        if (count === 2) return /^(2|two|double)\b/.test(value);
        return /^(3|three|triple)\b/.test(value);
      });
    }

    readSelectedOptions() {
      return Array.from(
        this.variantPicker?.querySelectorAll('input[type="radio"][data-option-value]:checked') || []
      ).map((input) => input.dataset.optionValue || input.value);
    }

    findSelectedVariant() {
      const selectedOptions = this.readSelectedOptions();
      if (!selectedOptions.length || !Array.isArray(this.variantMap)) return null;

      return this.variantMap.find((variant) => (
        Array.isArray(variant.options)
        && variant.options.length === selectedOptions.length
        && variant.options.every((value, index) => String(value) === String(selectedOptions[index]))
      )) || null;
    }

    selectNativeAddonOption(radio) {
      if (!radio) return false;

      radio.checked = true;
      const variant = this.findSelectedVariant();
      if (!variant) {
        // Correctness fallback for products whose variant map is incomplete.
        // Normal jersey products use the local path below and never re-render.
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        return false;
      }

      this.syncVariantLocally(variant);
      return true;
    }

    syncVariantLocally(variant) {
      const variantId = String(variant.id || '');
      if (!variantId) return;

      const productInfo = this.closest('product-info') || document;
      productInfo.querySelectorAll('form[action*="/cart/add"] input[name="id"]').forEach((input) => {
        input.value = variantId;
        input.setAttribute('value', variantId);
      });

      const currentVariantScript = Array.from(
        this.variantPicker?.querySelectorAll('script[type="application/json"]') || []
      ).find((script) => !script.hasAttribute('data-jc-variant-map'));
      if (currentVariantScript) currentVariantScript.textContent = JSON.stringify(variant);

      const suffix = this.formId.replace(/^ProductForm-/, '');
      const priceCents = Number(variant.price) || 0;
      const compareAtPriceCents = Number(variant.compare_at_price) || 0;
      const formattedPrice = this.formatMoney(priceCents);
      const formattedCompareAtPrice = this.formatMoney(compareAtPriceCents);

      const updatePriceRoot = (root) => {
        if (!root) return;
        const currentPrice = root.querySelector('.price__regular .trans-money');
        const compareAtPrice = root.querySelector('.price__sale .trans-money');
        if (currentPrice) currentPrice.textContent = formattedPrice;
        if (compareAtPrice && compareAtPriceCents > 0) compareAtPrice.textContent = formattedCompareAtPrice;
        root.querySelector('.price')?.classList.toggle(
          'price--on-sale',
          compareAtPriceCents > priceCents
        );
      };

      updatePriceRoot(document.getElementById(`Price-${suffix}`));
      updatePriceRoot(document.getElementById(`StickyPrice-${suffix}`));

      const buyButtonPrice = document.getElementById(`BuyButtonPrice-${suffix}`);
      const productBuyPrice = buyButtonPrice?.querySelector('product-buy-price');
      if (productBuyPrice) {
        productBuyPrice.dataset.price = String(priceCents);
        productBuyPrice.querySelector('.trans-money')?.replaceChildren(formattedPrice);
      }

      const form = document.querySelector(`[id="${this.formId}"]`);
      const addButton = document.querySelector(`button[name="add"][form="${this.formId}"]`)
        || form?.querySelector('button[name="add"]');
      if (addButton) {
        addButton.disabled = variant.available === false;
        addButton.setAttribute('aria-disabled', String(variant.available === false));
      }

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variantId);
      window.history.replaceState(window.history.state, '', url);

      this.dispatchEvent(new CustomEvent('jersey:variant-sync', {
        bubbles: true,
        detail: { variantId, variant }
      }));
    }

    bindEvents() {
      this.addEventListener('change', (event) => {
        event.stopPropagation();
      });

      // Text-field Enter is input navigation only. Prevent the browser from
      // treating it as an implicit product-form submit and firing Add to cart.
      const preventImplicitCustomizerSubmit = (event) => {
        const isCustomizerTextInput = event.target === this.nameInput
          || event.target === this.numberInput;
        const isEnter = event.key === 'Enter'
          || event.code === 'Enter'
          || event.keyCode === 13
          || event.inputType === 'insertLineBreak'
          || event.inputType === 'insertParagraph';
        if (!isCustomizerTextInput || !isEnter) return;

        event.preventDefault();
        event.stopPropagation();

        if (event.type === 'keydown') {
          if (event.target === this.nameInput && this.numberInput) {
            this.numberInput.focus({ preventScroll: true });
          } else {
            event.target.blur();
          }
        }
      };

      this.addEventListener('keydown', preventImplicitCustomizerSubmit, true);
      this.addEventListener('keypress', preventImplicitCustomizerSubmit, true);
      this.addEventListener('beforeinput', preventImplicitCustomizerSubmit, true);

      this.querySelector('[data-jc-custom-toggle]')?.addEventListener('click', (event) => {
        const toggle = event.currentTarget;
        this.setCustomOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });

      this.querySelector('[data-jc-clear]')?.addEventListener('click', () => this.clearCustomization());

      this.querySelectorAll('[data-jc-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this.selectTab(tab.dataset.jcTab));
      });

      this.nameInput?.addEventListener('input', () => {
        this.nameInput.value = this.nameInput.value.toUpperCase().slice(0, 15);
        if (this.playerSelect) {
          this.playerSelect.value = '';
          this.syncPlayerPicker();
        }
        this.evaluateCustomization({ allowVariantChange: true });
      });

      this.numberInput?.addEventListener('input', () => {
        this.numberInput.value = this.numberInput.value.replace(/\D/g, '').slice(0, 2);
        if (this.playerSelect) {
          this.playerSelect.value = '';
          this.syncPlayerPicker();
        }
        this.evaluateCustomization({ allowVariantChange: true });
      });

      this.playerSelect?.addEventListener('change', () => {
        this.syncPlayerPicker();
        const option = this.playerSelect.selectedOptions[0];
        if (!option?.value) {
          if (this.nameInput) this.nameInput.value = '';
          if (this.numberInput) this.numberInput.value = '';
          this.evaluateCustomization({ allowVariantChange: true });
          return;
        }
        if (this.nameInput) this.nameInput.value = String(option.dataset.playerName || '').toUpperCase().slice(0, 15);
        if (this.numberInput) this.numberInput.value = String(option.dataset.playerNumber || '').replace(/\D/g, '').slice(0, 2);
        this.evaluateCustomization({ allowVariantChange: true });
      });

      this.querySelector('[data-jc-patch-toggle]')?.addEventListener('click', (event) => {
        const toggle = event.currentTarget;
        this.setPatchOpen(toggle.getAttribute('aria-expanded') !== 'true');
      });

      this.patchInputs.forEach((input) => {
        input.addEventListener('change', () => {
          if (input.checked && this.patchLimit === 1) {
            this.patchInputs.forEach((other) => {
              if (other !== input) other.checked = false;
            });
          }

          const selected = this.patchInputs.filter((item) => item.checked);
          if (selected.length > this.patchLimit) input.checked = false;
          this.saveState();
          this.updatePatchState({ allowVariantChange: true });
        });
      });

    }

    evaluateCustomization({ allowVariantChange = false } = {}) {
      const name = this.nameInput?.value.trim() || '';
      const number = this.numberInput?.value.trim() || '';
      const hasName = name.length > 0;
      const hasNumber = /^[0-9]{1,2}$/.test(number);
      const complete = hasName && hasNumber;

      if (this.nameInput) {
        this.nameInput.setCustomValidity(!hasName && number ? 'Please enter the name for this jersey.' : '');
      }
      if (this.numberInput) {
        this.numberInput.setCustomValidity(hasName && !hasNumber ? 'Please enter a 1 or 2 digit jersey number.' : '');
      }

      this.setPropertySubmission(complete);
      this.customCard?.classList.toggle('is-active', complete);
      this.updateCustomizationPrice(complete);
      this.updateCustomizationSummary();
      this.saveState({ active: complete });

      if (!allowVariantChange) return;
      if (complete && this.yesRadio && !this.yesRadio.checked) {
        this.saveState({ active: true, open: true });
        this.selectNativeAddonOption(this.yesRadio);
      } else if (!complete && this.noRadio && !this.noRadio.checked) {
        this.saveState({ active: false });
        this.selectNativeAddonOption(this.noRadio);
      }
    }

    clearCustomization() {
      if (this.nameInput) this.nameInput.value = '';
      if (this.numberInput) this.numberInput.value = '';
      if (this.playerSelect) this.playerSelect.value = '';
      this.syncPlayerPicker();
      this.setPlayerPickerOpen(false);
      this.setPropertySubmission(false);
      this.customCard?.classList.remove('is-active');
      this.updateCustomizationPrice(false);
      this.setCustomOpen(false);
      this.updateCustomizationSummary();
      this.saveState({ active: false, open: false, name: '', number: '', playerValue: '' });
      if (this.noRadio && !this.noRadio.checked) {
        this.selectNativeAddonOption(this.noRadio);
      }
    }

    syncCustomizationState() {
      const selectedYes = Boolean(this.yesRadio?.checked || /^yes\b/i.test(this.dataset.customizeSelected || ''));
      const state = stateStore.get(this.stateKey);
      if (selectedYes) {
        this.customCard?.classList.add('is-active');
        if (!state) this.setCustomOpen(true, false);
      }
    }

    setPatchOpen(open, persist = true, animate = true) {
      const card = this.querySelector('[data-jc-patch-card]');
      const body = this.querySelector('[data-jc-patch-body]');
      const toggle = this.querySelector('[data-jc-patch-toggle]');
      if (!card || !body) return;

      const shouldOpen = Boolean(open);
      this.animateDrawer(card, body, toggle, shouldOpen, animate);
      if (shouldOpen) this.warmPatchImages();
      if (persist) this.saveState({ patchOpen: shouldOpen });
    }

    cancelDrawerMotion(body) {
      const motion = this.drawerAnimations?.get(body);
      if (!motion) return;

      this.drawerAnimations.delete(body);
      motion.animations.forEach((animation) => animation.cancel());
      motion.ghost?.remove();
      motion.card?.classList.remove('is-drawer-animating');
      motion.body?.classList.remove('is-drawer-animating');
      motion.shiftTargets?.forEach((element) => element.classList.remove('jc-drawer-shift'));
    }

    collectDrawerShiftTargets(card, includeInternal = false) {
      const targets = new Set();
      const productInfo = this.closest('product-info')
        || this.closest('.product__info-container')
        || this.parentElement?.parentElement;

      if (includeInternal) {
        const summary = card.querySelector('[data-jc-patch-summary]:not([hidden]), [data-jc-custom-summary]:not([hidden])');
        if (summary) targets.add(summary);
      }

      let node = card;
      while (node?.parentElement && node !== productInfo) {
        let sibling = node.nextElementSibling;
        while (sibling) {
          const rect = sibling.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0 && window.getComputedStyle(sibling).position !== 'fixed') {
            targets.add(sibling);
          }
          sibling = sibling.nextElementSibling;
        }
        node = node.parentElement;
      }

      return Array.from(targets);
    }

    createDrawerGhost(card, rect) {
      const ghost = card.cloneNode(true);
      ghost.classList.add('jc-drawer-ghost');
      ghost.classList.remove('is-drawer-animating');
      ghost.setAttribute('aria-hidden', 'true');
      ghost.setAttribute('inert', '');
      ghost.querySelectorAll('[id], [name]').forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('name');
      });
      ghost.style.setProperty('--jc-drawer-ghost-width', `${rect.width}px`);
      ghost.style.setProperty('--jc-drawer-ghost-height', `${rect.height}px`);
      ghost.style.top = `${rect.top}px`;
      ghost.style.left = `${rect.left}px`;
      document.body.append(ghost);
      return ghost;
    }

    animateShiftTargets(targets, beforeRects, duration, easing, animations) {
      targets.forEach((element) => {
        const before = beforeRects.get(element);
        const after = element.getBoundingClientRect();
        if (!before || after.width <= 0 || after.height <= 0) return;

        const deltaY = before.top - after.top;
        if (Math.abs(deltaY) < 0.5) return;

        const baseTransform = window.getComputedStyle(element).transform;
        const fromTransform = baseTransform === 'none'
          ? `translate3d(0, ${deltaY}px, 0)`
          : `translate3d(0, ${deltaY}px, 0) ${baseTransform}`;
        const toTransform = baseTransform === 'none'
          ? 'translate3d(0, 0, 0)'
          : baseTransform;

        element.classList.add('jc-drawer-shift');
        animations.push(element.animate(
          [
            { transform: fromTransform },
            { transform: toTransform }
          ],
          {
            duration,
            easing,
            fill: 'both'
          }
        ));
      });
    }

    animateDrawer(card, body, toggle, open, animate = true) {
      if (!card || !body) return;

      const shouldOpen = Boolean(open);
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.cancelDrawerMotion(body);

      toggle?.setAttribute('aria-expanded', String(shouldOpen));

      if (!animate || reduceMotion || typeof card.animate !== 'function') {
        card.classList.toggle('is-open', shouldOpen);
        body.toggleAttribute('inert', !shouldOpen);
        body.setAttribute('aria-hidden', String(!shouldOpen));
        return;
      }

      // Keep the established customizer pace, but give the taller patch drawer
      // a touch more time to unfold without changing the closing response.
      const isPatchDrawer = card.classList.contains('jc-patch-card');
      const duration = shouldOpen ? (isPatchDrawer ? 500 : 460) : 350;
      const contentDuration = shouldOpen ? (isPatchDrawer ? 420 : 380) : 280;
      const easing = 'cubic-bezier(0.32, 0.72, 0, 1)';
      const beforeCard = card.getBoundingClientRect();
      const shiftTargets = this.collectDrawerShiftTargets(card, shouldOpen);
      const beforeRects = new Map(
        shiftTargets.map((element) => [element, element.getBoundingClientRect()])
      );
      const ghost = shouldOpen ? null : this.createDrawerGhost(card, beforeCard);

      card.classList.toggle('is-open', shouldOpen);
      body.toggleAttribute('inert', !shouldOpen);
      body.setAttribute('aria-hidden', String(!shouldOpen));

      const afterCard = card.getBoundingClientRect();
      const heightDelta = Math.max(0, Math.abs(afterCard.height - beforeCard.height));
      if (heightDelta < 0.5) {
        ghost?.remove();
        return;
      }

      card.classList.add('is-drawer-animating');
      body.classList.add('is-drawer-animating');

      const animations = [];
      const radius = window.getComputedStyle(card).borderRadius || '0px';

      if (shouldOpen) {
        animations.push(card.animate(
          [
            { clipPath: `inset(0 0 ${heightDelta}px 0 round ${radius})` },
            { clipPath: `inset(0 0 0 0 round ${radius})` }
          ],
          {
            duration,
            easing,
            fill: 'both'
          }
        ));

        animations.push(body.animate(
          [
            { opacity: 0, transform: 'translate3d(0, -4px, 0)' },
            { opacity: 1, transform: 'translate3d(0, 0, 0)' }
          ],
          {
            duration: contentDuration,
            easing,
            fill: 'both'
          }
        ));
      } else if (ghost) {
        animations.push(ghost.animate(
          [
            { clipPath: `inset(0 0 0 0 round ${radius})` },
            { clipPath: `inset(0 0 ${heightDelta}px 0 round ${radius})` }
          ],
          {
            duration,
            easing,
            fill: 'both'
          }
        ));

        const ghostBody = ghost.querySelector('[data-jc-custom-body], [data-jc-patch-body]');
        if (ghostBody) {
          animations.push(ghostBody.animate(
            [
              { opacity: 1, transform: 'translate3d(0, 0, 0)' },
              { opacity: 0, transform: 'translate3d(0, -4px, 0)' }
            ],
            {
              duration: contentDuration,
              easing,
              fill: 'both'
            }
          ));
        }

        const ghostSummary = ghost.querySelector('[data-jc-patch-summary]:not([hidden])');
        if (ghostSummary) {
          animations.push(ghostSummary.animate(
            [
              { transform: 'translate3d(0, 0, 0)' },
              { transform: `translate3d(0, -${heightDelta}px, 0)` }
            ],
            {
              duration,
              easing,
              fill: 'both'
            }
          ));
        }
      }

      this.animateShiftTargets(shiftTargets, beforeRects, duration, easing, animations);

      const motion = {
        animations,
        ghost,
        card,
        body,
        shiftTargets
      };
      this.drawerAnimations.set(body, motion);

      const finish = () => {
        if (this.drawerAnimations.get(body) !== motion) return;
        this.drawerAnimations.delete(body);
        animations.forEach((animation) => animation.cancel());
        ghost?.remove();
        card.classList.remove('is-drawer-animating');
        body.classList.remove('is-drawer-animating');
        shiftTargets.forEach((element) => element.classList.remove('jc-drawer-shift'));
      };

      Promise.all(animations.map((animation) => animation.finished.catch(() => null))).then(finish);
    }

    setPropertySubmission(enabled) {
      [this.customProperty, this.nameInput, this.numberInput].forEach((input) => {
        if (!input) return;
        const propertyName = input.dataset.propertyName;
        if (enabled && propertyName) input.setAttribute('name', propertyName);
        else input.removeAttribute('name');
      });
      [this.nameInput, this.numberInput].forEach((input) => {
        if (input) input.required = enabled;
      });
    }

    setCustomOpen(open, persist = true, animate = true) {
      if (!this.customCard || !this.customBody) return;
      const shouldOpen = Boolean(open);
      const toggle = this.querySelector('[data-jc-custom-toggle]');
      this.animateDrawer(this.customCard, this.customBody, toggle, shouldOpen, animate);
      if (!shouldOpen) this.setPlayerPickerOpen(false);
      if (persist) this.saveState({ open: shouldOpen });
    }

    selectTab(name) {
      const currentTab = this.querySelector('[data-jc-tab].is-active')?.dataset.jcTab;
      if (currentTab === name) return;

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const shouldAnimate = Boolean(
        this.customBody
        && this.customCard?.classList.contains('is-open')
        && !reduceMotion
      );
      const startHeight = shouldAnimate ? this.customBody.getBoundingClientRect().height : 0;

      if (this.tabHeightAnimation) {
        const previousHeightAnimation = this.tabHeightAnimation;
        this.tabHeightAnimation = null;
        previousHeightAnimation.cancel();
      }
      if (this.tabPanelAnimation) {
        const previousPanelAnimation = this.tabPanelAnimation;
        this.tabPanelAnimation = null;
        previousPanelAnimation.cancel();
      }
      if (this.customBody) {
        this.customBody.style.height = '';
        this.customBody.style.overflow = '';
      }

      const clearSelectedPlayer = name === 'manual' && Boolean(this.playerSelect?.value);

      this.querySelectorAll('[data-jc-tab]').forEach((tab) => {
        const selected = tab.dataset.jcTab === name;
        tab.classList.toggle('is-active', selected);
        tab.setAttribute('aria-selected', String(selected));
      });
      this.querySelectorAll('[data-jc-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.jcPanel !== name;
      });
      if (name !== 'player') this.setPlayerPickerOpen(false);

      const activePanel = this.querySelector(`[data-jc-panel="${name}"]`);
      if (shouldAnimate && activePanel) {
        const panelAnimation = activePanel.animate(
          [
            { opacity: 0, transform: 'translateY(5px)' },
            { opacity: 1, transform: 'translateY(0)' }
          ],
          {
            duration: 180,
            easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
            fill: 'both'
          }
        );
        this.tabPanelAnimation = panelAnimation;
        const finishPanelAnimation = () => {
          if (this.tabPanelAnimation !== panelAnimation) return;
          this.tabPanelAnimation = null;
          panelAnimation.cancel();
        };
        panelAnimation.addEventListener('finish', finishPanelAnimation, { once: true });
      }

      if (shouldAnimate) {
        const endHeight = this.customBody.getBoundingClientRect().height;
        if (Math.abs(endHeight - startHeight) > 0.5) {
          this.customBody.style.height = `${startHeight}px`;
          this.customBody.style.overflow = 'hidden';

          const heightAnimation = this.customBody.animate(
            [
              { height: `${startHeight}px` },
              { height: `${endHeight}px` }
            ],
            {
              duration: 210,
              easing: 'cubic-bezier(0.23, 1, 0.32, 1)',
              fill: 'both'
            }
          );
          this.tabHeightAnimation = heightAnimation;

          const finishHeightAnimation = () => {
            if (this.tabHeightAnimation !== heightAnimation) return;
            this.tabHeightAnimation = null;
            this.customBody.style.height = '';
            this.customBody.style.overflow = '';
            heightAnimation.cancel();
          };
          heightAnimation.addEventListener('finish', finishHeightAnimation, { once: true });
          heightAnimation.addEventListener('cancel', () => {
            if (this.tabHeightAnimation !== heightAnimation) return;
            this.tabHeightAnimation = null;
            this.customBody.style.height = '';
            this.customBody.style.overflow = '';
          }, { once: true });
        }
      }

      if (clearSelectedPlayer) {
        if (this.nameInput) this.nameInput.value = '';
        if (this.numberInput) this.numberInput.value = '';
        this.playerSelect.value = '';
        this.syncPlayerPicker();
        this.saveState({ tab: name, active: false, name: '', number: '', playerValue: '' });
        this.evaluateCustomization({ allowVariantChange: true });
        return;
      }

      this.saveState({ tab: name });
    }

    updateCustomizationSummary() {
      if (!this.customSummary) return;
      const name = this.nameInput?.value.trim();
      const number = this.numberInput?.value.trim();
      const complete = Boolean(name && /^[0-9]{1,2}$/.test(number || ''));
      if (!complete) {
        this.customSummary.hidden = true;
        this.customSummary.textContent = '';
        return;
      }
      this.customSummary.textContent = `${name} · #${number}`;
      this.customSummary.hidden = false;
    }

    updateCustomizationPrice(active) {
      if (!this.customAdd) return;
      this.customAdd.textContent = active ? `+ ${this.formatMoney(this.customPriceCents)}` : '+ Add';
      this.customAdd.classList.toggle('is-priced', active);
    }

    warmPatchImages() {
      this.patchInputs.slice(0, 4).forEach((input) => {
        const image = input.closest('[data-jc-patch-row]')?.querySelector('img');
        if (!image) return;
        image.loading = 'eager';
        image.decoding = 'async';
        image.fetchPriority = 'low';
        image.decode?.().catch(() => {});
      });
    }

    updatePatchState({ allowVariantChange = false } = {}) {
      const selected = this.patchInputs.filter((input) => input.checked);
      const atLimit = selected.length >= this.patchLimit;

      this.patchInputs.forEach((input) => {
        const shouldDisable = this.patchLimit > 1 && atLimit && !input.checked;
        input.disabled = shouldDisable;
        input.closest('[data-jc-patch-row]')?.classList.toggle('is-limit-disabled', shouldDisable);
      });

      const names = selected.map((input) => input.dataset.patchName).filter(Boolean);
      if (this.patchProperty) {
        this.patchProperty.value = names.join(' + ');
        this.patchProperty.disabled = names.length === 0;
      }

      if (this.patchSummary) {
        const chip = document.createElement('span');
        chip.className = names.length > 0 ? 'jc-chip' : 'jc-chip jc-chip--empty';
        chip.textContent = names.length > 0 ? names.join(' · ') : 'No patches selected';
        this.patchSummary.replaceChildren(chip);
        this.patchSummary.hidden = false;
      }

      if (this.patchAdd) {
        const totalCents = selected.reduce((total, input) => total + (Number(input.dataset.patchPriceCents) || 0), 0);
        this.patchAdd.textContent = totalCents > 0 ? `+ ${this.formatMoney(totalCents)}` : '+ Add';
        this.patchAdd.classList.toggle('is-priced', totalCents > 0);
      }

      this.querySelector('[data-jc-patch-card]')?.classList.toggle('is-active', selected.length > 0);

      if (!allowVariantChange) return;
      const targetRadio = selected.length === 0
        ? this.patchNoneRadio
        : selected.length === 1
          ? this.patchOneRadio
          : selected.length === 2
            ? this.patchTwoRadio
            : this.patchThreeRadio;
      if (targetRadio && !targetRadio.checked) {
        this.saveState();
        targetRadio.checked = true;
        this.selectNativeAddonOption(targetRadio);
      }
    }

    formatMoney(cents) {
      const currency = this.dataset.currency || 'USD';
      try {
        return new Intl.NumberFormat(document.documentElement.lang || 'en', {
          style: 'currency',
          currency
        }).format(cents / 100);
      } catch (_) {
        return `$${(cents / 100).toFixed(2)}`;
      }
    }

    saveState(overrides = {}) {
      const current = stateStore.get(this.stateKey) || {};
      stateStore.set(this.stateKey, {
        ...current,
        active: Boolean(this.customCard?.classList.contains('is-active')),
        open: Boolean(this.customCard?.classList.contains('is-open')),
        patchOpen: Boolean(this.querySelector('[data-jc-patch-card]')?.classList.contains('is-open')),
        name: this.nameInput?.value || '',
        number: this.numberInput?.value || '',
        tab: this.querySelector('[data-jc-tab].is-active')?.dataset.jcTab || 'manual',
        playerValue: this.playerSelect?.value || '',
        patches: this.patchInputs.filter((input) => input.checked).map((input) => input.value),
        ...overrides
      });
    }

    restoreState() {
      const state = stateStore.get(this.stateKey);
      if (!state) return;
      if (this.nameInput) this.nameInput.value = state.name || '';
      if (this.numberInput) this.numberInput.value = state.number || '';
      if (this.playerSelect && state.playerValue) this.playerSelect.value = state.playerValue;

      if (state.tab) {
        this.querySelectorAll('[data-jc-tab]').forEach((tab) => {
          const selected = tab.dataset.jcTab === state.tab;
          tab.classList.toggle('is-active', selected);
          tab.setAttribute('aria-selected', String(selected));
        });
        this.querySelectorAll('[data-jc-panel]').forEach((panel) => {
          panel.hidden = panel.dataset.jcPanel !== state.tab;
        });
      }

      this.patchInputs.forEach((input) => {
        input.checked = Array.isArray(state.patches) && state.patches.includes(input.value);
      });
      this.setCustomOpen(Boolean(state.open), false, false);
      this.setPatchOpen(Boolean(state.patchOpen), false, false);
    }
  }

  if (!customElements.get('jersey-customizer')) {
    customElements.define('jersey-customizer', JerseyCustomizer);
  }

  const mountMerchCard = (root = document) => {
    const searchRoot = root.querySelectorAll ? root : document;
    const stockNotes = Array.from(searchRoot.querySelectorAll('.jc-stock-note'));

    stockNotes.forEach((stockNote) => {
      if (stockNote.closest('.jc-merch-card')) return;

      const productInfo = stockNote.closest('product-info') || document;
      const promoImage = Array.from(productInfo.querySelectorAll('img.image-fit')).find((image) =>
        (image.alt || '').includes('Buy 2 Get 1 FREE, Buy 3 Get 2 FREE')
      );
      const promoInner = promoImage?.closest('.product__text-inner');
      const promoBlock = promoInner?.closest('.product__text');
      if (!promoInner || !promoBlock) return;

      const promoCopy = promoInner.querySelector('p');
      if (promoCopy && !promoCopy.querySelector('.jc-merch-card__deals')) {
        promoCopy.classList.add('jc-merch-card__copy');
        promoCopy.innerHTML = `
          <span class="jc-merch-card__eyebrow"><i aria-hidden="true"></i>Bundle &amp; save</span>
          <span class="jc-merch-card__deals">
            <span>Buy 2 <b>Get 1 free</b></span>
            <span>Buy 3 <b>Get 2 free</b></span>
          </span>
          <span class="jc-merch-card__sub">Discount applied automatically in cart</span>
        `;
      }

      promoInner.classList.add('jc-merch-card__promo');
      const card = document.createElement('div');
      card.className = 'jc-merch-card';
      stockNote.parentNode.insertBefore(card, stockNote);
      card.append(promoInner, stockNote);
      promoBlock.hidden = true;
      promoBlock.setAttribute('aria-hidden', 'true');
    });
  };

  let merchMountFrame = 0;
  const scheduleMerchCard = (root = document) => {
    if (merchMountFrame) cancelAnimationFrame(merchMountFrame);
    merchMountFrame = requestAnimationFrame(() => {
      merchMountFrame = 0;
      mountMerchCard(root);
    });
  };

  const startCartFlight = () => {
    mountCartFlight(document);
    scheduleMerchCard(document);
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startCartFlight, { once: true });
  } else {
    startCartFlight();
  }
  document.addEventListener('shopify:section:load', (event) => {
    mountCartFlight(event.target);
    scheduleMerchCard(event.target);
  });
})();
